import { action, makeObservable, observable, runInAction, computed } from 'mobx';
import {
  DEFAULT_WAITING_ANIMATION_PREFERENCES,
  isWaitingChoice,
  WAITING_CHOICE_RANDOM,
  type IWaitingAnimationPreferences,
  type WaitingChoiceType,
} from '@featit/shared/brand/waiting-motion';
import {
  failureToSay,
  type HeldSentenceType,
  pageWords,
  sayFailure,
} from '@/services/actionFailure';
import { expectRecord, UnreadableAnswerError } from '@/services/answerShape';
import { FIXED_STUDY_KEY } from '@/components/waiting/waitingSession';

export interface IWaitingAnimationApi {
  waitingAnimation(): Promise<IWaitingAnimationPreferences>;
  updateWaitingAnimation(study: WaitingChoiceType): Promise<IWaitingAnimationPreferences>;
}

type StorageType = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * F-0578 WAIT-5 (`docs/concept-waiting-motion.md`): *Waiting animation*, Random or one study — a
 * person's setting, kept with the person on the server (`users.preferences.waitingAnimation`) so
 * every device they sign in on draws the same, and cached on the device (`featit:waiting:study`)
 * so a launch reads it without a request (`waitingSession.ts`). A choice applies from the next
 * launch: this launch's waits keep the study they started with (WAIT-4).
 *
 * Choosing applies at once and is then saved; a refused save puts the last choice back and says
 * so. An answer from the server that arrives after a choice made here is not applied over it.
 */
export class WaitingAnimationStore {
  study: WaitingChoiceType;
  saving = false;
  /** T-0471: what `error` says, kept, so it is written in the page's language when read. */
  heldError: HeldSentenceType | null = null;
  get error(): string | null {
    return sayFailure(pageWords(), this.heldError);
  }
  set error(value: HeldSentenceType | null) {
    this.heldError = value;
  }
  private loadedFor: string | null = null;
  private choices = 0;

  constructor(
    private readonly options: {
      userId: () => string | null;
      api: IWaitingAnimationApi;
      storage: () => StorageType | null;
    },
  ) {
    this.study = this.cached() ?? DEFAULT_WAITING_ANIMATION_PREFERENCES.study;
    makeObservable<WaitingAnimationStore, 'adopt'>(this, {
      study: observable,
      saving: observable,
      heldError: observable.ref,
      error: computed,
      adopt: action,
      setStudy: action,
    });
  }

  /** The signed-in person's setting, once per person; a failed read keeps the device's copy. */
  async load(): Promise<void> {
    const userId = this.options.userId();
    if (!userId || this.loadedFor === userId) return;
    this.loadedFor = userId;
    const choices = this.choices;
    try {
      const preferences = expectRecord<IWaitingAnimationPreferences>(
        await this.options.api.waitingAnimation(),
        'Your waiting animation setting',
        ['study'],
      );
      if (!isWaitingChoice(preferences.study)) {
        throw new UnreadableAnswerError('Your waiting animation setting');
      }
      if (this.loadedFor !== userId || this.options.userId() !== userId) return;
      if (this.choices !== choices) return;
      this.adopt(preferences.study);
    } catch {
      if (this.loadedFor === userId) this.loadedFor = null;
    }
  }

  forget(): void {
    this.loadedFor = null;
  }

  async setStudy(study: WaitingChoiceType): Promise<void> {
    if (study === this.study) return;
    const previous = this.study;
    this.choices += 1;
    this.adopt(study);
    this.error = null;
    if (!this.options.userId()) return;
    this.saving = true;
    try {
      await this.options.api.updateWaitingAnimation(study);
    } catch (error) {
      runInAction(() => {
        if (this.study === study) this.adopt(previous);
        this.error = failureToSay(error, 'settingsAccount.appearance.actions.saveWaitingAnimation');
      });
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  private adopt(study: WaitingChoiceType): void {
    this.study = study;
    try {
      const storage = this.options.storage();
      // Random is the absence of a fixed study: `pickWaitingStudy` reads anything else as Random.
      if (study === WAITING_CHOICE_RANDOM) storage?.removeItem(FIXED_STUDY_KEY);
      else storage?.setItem(FIXED_STUDY_KEY, study);
    } catch {
      // Storage unavailable: the next launch picks at random until the read lands.
    }
  }

  private cached(): WaitingChoiceType | null {
    try {
      const stored = this.options.storage()?.getItem(FIXED_STUDY_KEY);
      return isWaitingChoice(stored) ? stored : null;
    } catch {
      return null;
    }
  }
}
